import axios from 'axios';

// Utiliza variável de ambiente com fallback para desenvolvimento local
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Interceptor para adicionar token JWT
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor para tratamento de erros
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ==================== PATIENTS ====================

export const patientsService = {
  getAll: async () => {
    const response = await apiClient.get('/patients');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/patients/${id}`);
    return response.data;
  },

  create: async (patientData) => {
    const response = await apiClient.post('/patients', patientData);
    return response.data;
  },

  update: async (id, patientData) => {
    const response = await apiClient.put(`/patients/${id}`, patientData);
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/patients/${id}`);
    return response.data;
  },

  getExams: async (patientId) => {
    const response = await apiClient.get(`/patients/${patientId}/exams`);
    return response.data;
  },
};

// ==================== EXAMS ====================

export const examsService = {
  getAll: async () => {
    const response = await apiClient.get('/exams');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/exams/${id}`);
    return response.data;
  },

  create: async (examData) => {
    const response = await apiClient.post('/exams', examData);
    return response.data;
  },

  update: async (id, examData) => {
    const response = await apiClient.put(`/exams/${id}`, examData);
    return response.data;
  },

  getFiles: async (examId) => {
    const response = await apiClient.get(`/exams/${examId}/files`);
    return response.data;
  },
};

// ==================== OCCUPATIONAL EXAMS ====================

export const occupationalExamsService = {
  create: async (examData) => {
    const response = await apiClient.post('/occupationalexams', examData);
    return response.data;
  },

  getById: async (examId) => {
    const response = await apiClient.get(`/occupationalexams/${examId}`);
    return response.data;
  },

  generateReport: async (examId) => {
    const response = await apiClient.post(`/occupationalexams/${examId}/generate-report`);
    return response.data;
  },
};

// ==================== REPORTS ====================

export const reportsService = {
  getAll: async () => {
    const response = await apiClient.get('/reports');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/reports/${id}`);
    return response.data;
  },

  create: async (reportData) => {
    const response = await apiClient.post('/reports', reportData);
    return response.data;
  },

  update: async (id, reportData) => {
    const response = await apiClient.put(`/reports/${id}`, reportData);
    return response.data;
  },

  downloadPdf: async (id) => {
    const response = await apiClient.get(`/reports/${id}/pdf`, {
      responseType: 'blob',
    });
    return response.data;
  },

  generate: async (examId, payload) => {
    const response = await apiClient.post(`/reports/generate/${examId}`, payload);
    return response.data;
  },

  download: async (id) => {
    const response = await apiClient.get(`/reports/${id}/download`, {
      responseType: 'blob',
    });
    return response.data;
  },

  getByPatient: async (patientId) => {
    const response = await apiClient.get(`/reports/patient/${patientId}`);
    return response.data;
  },
};

// ==================== FILES ====================

export const filesService = {
  upload: async (formData) => {
    const response = await apiClient.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  getByExam: async (examId) => {
    const response = await apiClient.get(`/files/exam/${examId}`);
    return response.data;
  },
};

// ==================== USERS ====================

export const usersService = {
  getProfile: async () => {
    const response = await apiClient.get('/users/me/profile');
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await apiClient.put('/users/me/profile', profileData);
    return response.data;
  },
};

// ==================== AUTH (Placeholder) ====================

export const authService = {
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },
};

// ==================== EQUIPMENTS ====================

export const equipmentsService = {
  getAll: async () => {
    const response = await apiClient.get('/equipments');
    return response.data;
  },

  getById: async (id) => {
    const response = await apiClient.get(`/equipments/${id}`);
    return response.data;
  },

  create: async (equipment) => {
    const response = await apiClient.post('/equipments', equipment);
    return response.data;
  },

  update: async (id, equipment) => {
    const response = await apiClient.put(`/equipments/${id}`, equipment);
    return response.data;
  },

  toggleStatus: async (id) => {
    const response = await apiClient.patch(`/equipments/${id}/toggle-status`);
    return response.data;
  },

  delete: async (id) => {
    await apiClient.delete(`/equipments/${id}`);
  },
};

export default apiClient;
